import React, { useEffect, useRef } from 'react';
import { ArrowRight, Play } from 'lucide-react';
import { Button } from './ui/button';

export const Hero = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const scrollToProjects = () => {
    document.querySelector('#projects')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Cinematic Parallax Effect */}
      <div className="absolute inset-0 z-0">
        <div
          className="absolute inset-0 bg-cover bg-center transform"
          style={{
            backgroundImage: `url('https://images.pexels.com/photos/36271831/pexels-photo-36271831.jpeg')`,
            animation: 'slowZoom 25s ease-in-out infinite alternate'
          }}
        />
        {/* Enhanced Gradient Overlays with Gold Tint */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A1F3D]/98 via-[#0A1F3D]/90 to-[#0A1F3D]/75" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F3D]/95 via-[#0A1F3D]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#C9A14A]/8 via-transparent to-[#C9A14A]/5" />
        {/* Subtle Vignette */}
        <div className="absolute inset-0 shadow-[inset_0_0_100px_rgba(10,31,61,0.6)]" />
      </div>

      {/* Animated Particles */}
      <div className="absolute inset-0 z-10">
        <div className="particle particle-1" />
        <div className="particle particle-2" />
        <div className="particle particle-3" />
      </div>

      {/* Content */}
      <div ref={heroRef} className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center opacity-0">
        <div className="space-y-6 sm:space-y-10">
          {/* Main Heading with Enhanced Typography */}
          <h1 className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-tight tracking-tight">
            Premium Real Estate Services
            <br />
            <span className="text-[#C9A14A] inline-block mt-1 sm:mt-2">in Secunderabad</span>
          </h1>

          {/* Subheading with Better Spacing */}
          <p className="text-base sm:text-xl md:text-2xl lg:text-3xl text-gray-100 max-w-4xl mx-auto font-light leading-relaxed tracking-wide px-2">
            Buy, Sell & Invest in High-Value Properties with Trusted Experts
          </p>

          {/* CTA Buttons with Enhanced Styling */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mt-8 sm:mt-16 px-4">
            <Button
              onClick={scrollToProjects}
              size="lg"
              className="w-full sm:w-auto bg-[#C9A14A] hover:bg-[#B89140] text-[#0A1F3D] font-bold text-base sm:text-lg px-8 sm:px-10 py-5 sm:py-7 rounded-full shadow-2xl hover:shadow-[0_20px_60px_rgba(201,161,74,0.4)] transition-all duration-500 hover:scale-110 hover:-translate-y-1"
            >
              Explore Projects
              <ArrowRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            <Button
              onClick={scrollToContact}
              size="lg"
              variant="outline"
              className="w-full sm:w-auto border-2 border-white/80 text-white hover:bg-white hover:text-[#0A1F3D] font-bold text-base sm:text-lg px-8 sm:px-10 py-5 sm:py-7 rounded-full backdrop-blur-md bg-white/5 transition-all duration-500 hover:scale-110 hover:-translate-y-1 hover:border-white"
            >
              Contact Now
              <Play className="ml-2 w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
          </div>

          {/* Trust Indicators with Premium Styling */}
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-12 mt-12 sm:mt-20 text-white px-4">
            <div className="text-center group">
              <div className="text-3xl sm:text-5xl md:text-6xl font-bold text-[#C9A14A] group-hover:scale-110 transition-transform duration-300">15+</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-200 mt-1 sm:mt-2 tracking-wider uppercase">Years Experience</div>
            </div>
            <div className="h-12 sm:h-16 w-px bg-gradient-to-b from-transparent via-gray-500 to-transparent" />
            <div className="text-center group">
              <div className="text-3xl sm:text-5xl md:text-6xl font-bold text-[#C9A14A] group-hover:scale-110 transition-transform duration-300">500+</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-200 mt-1 sm:mt-2 tracking-wider uppercase">Happy Clients</div>
            </div>
            <div className="h-12 sm:h-16 w-px bg-gradient-to-b from-transparent via-gray-500 to-transparent" />
            <div className="text-center group">
              <div className="text-3xl sm:text-5xl md:text-6xl font-bold text-[#C9A14A] group-hover:scale-110 transition-transform duration-300">50+</div>
              <div className="text-xs sm:text-sm md:text-base text-gray-200 mt-1 sm:mt-2 tracking-wider uppercase">Projects Delivered</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="animate-bounce">
          <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse" />
          </div>
        </div>
      </div>
    </section>
  );
};
